import type { SourceAuthorityType } from '../../data-profiles/sourceAuthorityTypes';
import { SOURCE_AUTHORITY_TYPE_REGISTRY } from '../../data-profiles/sourceAuthorityTypes';
import type { FieldPack } from '../types';
import type { FieldSensitivity } from '../types/FieldManifest';
import {
  getCanonicalPredicateForAlias,
  getRelationPredicateDefinition,
} from '../../ontology/RelationPredicateRegistry';
import {
  KNOWN_PROTECTED_FIELD_KEYS,
  fieldSensitivityRank,
} from './fieldSensitivity';

export const FOLDER_RECORD_PROJECTION_POLICY_VERSION = 'folder-data-projection.v1';

const STRUCTURAL_KEYS = new Set([
  'id',
  'externalId',
  'name',
  'title',
  'nodeType',
  'type',
  '_sourceBinding',
  'runId',
  'lifecycle',
  // Repository-gate control metadata. It is infrastructure provenance (the
  // same class as _sourceBinding), not a domain field requiring a data grant.
  // Projections retain it for precedence/diff consumers without treating an
  // otherwise complete invariant scope as authorization-redacted.
  '_fieldProvenance',
  // Permissioned projections return only the value-free control envelope;
  // private workflow actor fields remain filtered from the canonical row.
  '_atlasAssertion',
]);
const SAFE_NESTED_CONTAINER_KEYS = new Set(['rowSnapshot', 'attrs', 'props', 'qualifiers']);

/** Repository-owned properties that user-authored record payloads may not set. */
export const FOLDER_RECORD_SYSTEM_PROPERTY_KEYS = new Set([
  '_sourceBinding',
  '_fieldProvenance',
  '_atlasAssertion',
  '_effectiveAssertion',
  '_retirement',
  '_tombstone',
  'lifecycle',
  'runId',
]);

const SOURCE_BINDING_KEYS = new Set([
  'bindingId',
  'runId',
  'connectorId',
  'dataSourceId',
  'sourceTableId',
  'sourceLabel',
  'selectedIntent',
  'tombstonePolicy',
  'status',
  'tombstonedAt',
  'tombstonedRunId',
  'tombstoneRunId',
  'missingFactDecisionId',
]);

const LIFECYCLE_KEYS = new Set([
  'state', 'source', 'setAt', 'setBy', 'setByName', 'reason', 'agentRun',
]);
const LIFECYCLE_STATES = new Set([
  'draft', 'needs_review', 'approved', 'declined', 'stale', 'retired',
]);
const LIFECYCLE_SOURCES = new Set(['computed', 'manual', 'agent', 'system']);
const AGENT_RUN_KINDS = new Set(['compass_audit', 'workflow', 'tool_call']);

export const ORG_PLANNING_ASSERTION_FIELD_KEY = '_atlasAssertion';
export const ORG_PLANNING_ASSERTION_FIELD_PACK_ID = 'org-planning-assertion-control';

const ORG_PLANNING_SOURCE_TYPES: readonly SourceAuthorityType[] = [
  'people_identity',
  'position_structure',
  'team_membership',
  'project_allocation',
  'capacity_commitment',
  'workforce_demand',
  'skill_catalog',
  'skill_assertion',
  'credential_assertion',
  'skill_requirement',
  'talent_observation',
  'succession_plan',
];

const PROJECTED_ASSERTION_KEYS = new Set([
  'schemaVersion',
  'assertionId',
  'changeSetId',
  'subjectRecordId',
  'supersedesFactId',
  'operation',
  'sourceType',
  'validFrom',
  'validTo',
  'baseFingerprint',
  'assertedFingerprint',
  'reconciliationState',
  'reconciledAt',
  'approvedAt',
  'conflictState',
  'status',
  'migrationKey',
]);

export interface FolderRecordFieldClassification {
  sensitivity: FieldSensitivity;
  fieldPackIds: string[];
  sourceTypes: SourceAuthorityType[];
  origin: 'structural' | 'registered' | 'known_protected' | 'unclassified';
}

/**
 * One classification registry shared by canonical write sanitization and
 * every permissioned projection. Unknown read fields fail closed until a
 * mapping has been reviewed.
 */
export class FolderRecordProtectionRegistry {
  private readonly fieldsByProfile = new Map<
    string,
    Map<string, FolderRecordFieldClassification>
  >();

  /**
   * The nested-key check runs once per key per record on both the write path
   * (snapshot sanitization) and the read path (container escalation) — for a
   * 200-row/124-column import that is hundreds of thousands of calls over a
   * key space of ~130 distinct names. The registry is immutable after
   * construction, so the answer per key never changes.
   */
  private readonly nestedKeyCache = new Map<string, FolderRecordFieldClassification | null>();

  constructor(fieldPacks: Readonly<Record<string, readonly FieldPack[]>>) {
    for (const [profileId, packs] of Object.entries(fieldPacks)) {
      this.fieldsByProfile.set(profileId, buildFieldIndex(profileId, packs));
    }
  }

  classifyProperty(profileId: string, key: string): FolderRecordFieldClassification {
    if (STRUCTURAL_KEYS.has(key)) {
      return {
        sensitivity: 'internal',
        fieldPackIds: [`structural:${profileId}`],
        sourceTypes: defaultSourceTypes(profileId),
        origin: 'structural',
      };
    }

    const leaf = key.includes('.') ? key.slice(key.lastIndexOf('.') + 1) : key;
    const registered = this.fieldsByProfile.get(profileId)?.get(key);
    const knownProtected = KNOWN_PROTECTED_FIELD_KEYS.has(key) || KNOWN_PROTECTED_FIELD_KEYS.has(leaf)
      ? {
        sensitivity: sensitivityForKnownProtectedKey(key),
        fieldPackIds: [`protected:${profileId}`],
        sourceTypes: defaultSourceTypes(profileId),
        origin: 'known_protected' as const,
      }
      : null;
    if (registered && knownProtected) {
      return fieldSensitivityRank(knownProtected.sensitivity) > fieldSensitivityRank(registered.sensitivity)
        ? knownProtected
        : registered;
    }
    if (registered) return registered;
    if (knownProtected) return knownProtected;

    // Unknown fields fail closed at HR level. Nobody has reviewed what an
    // unmapped imported column contains, so it must not be readable below an
    // explicit high-sensitivity grant until a classification exists.
    return {
      sensitivity: 'hr_restricted',
      fieldPackIds: [`unclassified:${profileId}`],
      sourceTypes: defaultSourceTypes(profileId),
      origin: 'unclassified',
    };
  }

  /** Cross-profile fail-closed classification for keys nested in containers. */
  protectedNestedKeyClassification(key: string): FolderRecordFieldClassification | null {
    const cached = this.nestedKeyCache.get(key);
    if (cached !== undefined || this.nestedKeyCache.has(key)) return cached ?? null;
    const leaf = key.includes('.') ? key.slice(key.lastIndexOf('.') + 1) : key;
    let match: FolderRecordFieldClassification | null =
      STRUCTURAL_KEYS.has(key) || SAFE_NESTED_CONTAINER_KEYS.has(key)
      ? {
        sensitivity: 'internal',
        fieldPackIds: ['structural:nested'],
        sourceTypes: [],
        origin: 'structural',
      }
      :
      KNOWN_PROTECTED_FIELD_KEYS.has(key) || KNOWN_PROTECTED_FIELD_KEYS.has(leaf)
        ? {
          sensitivity: sensitivityForKnownProtectedKey(key),
          fieldPackIds: ['protected:nested'],
          sourceTypes: [],
          origin: 'known_protected',
        }
        : null;
    for (const fields of this.fieldsByProfile.values()) {
      const registered = fields.get(key) ?? fields.get(leaf);
      if (!registered) continue;
      if (!match || fieldSensitivityRank(registered.sensitivity) > fieldSensitivityRank(match.sensitivity)) {
        match = registered;
      }
    }
    if (!match) {
      match = {
        sensitivity: 'hr_restricted',
        fieldPackIds: ['unclassified:nested'],
        sourceTypes: [],
        origin: 'unclassified',
      };
    }
    // Keys come from imported data, so cap the cache rather than letting a
    // pathological corpus grow it without bound.
    if (this.nestedKeyCache.size < 10_000) this.nestedKeyCache.set(key, match);
    return match;
  }

  shouldPersistProtected(profileId: string, key: string): boolean {
    const classification = this.classifyProperty(profileId, key);
    return classification.origin !== 'unclassified'
      && fieldSensitivityRank(classification.sensitivity) >= fieldSensitivityRank('manager');
  }
}

/**
 * Registries are immutable and derived purely from their field packs, but
 * several construction sites are transient (per-request or per-transaction),
 * so each rebuilt the full field index and lost the nested-key cache. Sharing
 * one instance per field-pack object keeps the caches effective process-wide.
 * Callers must pass a module-level pack constant (not an inline literal) to
 * benefit.
 */
const SHARED_REGISTRIES = new WeakMap<object, FolderRecordProtectionRegistry>();

export function sharedFolderRecordProtectionRegistry(
  fieldPacks: Readonly<Record<string, readonly FieldPack[]>>,
): FolderRecordProtectionRegistry {
  const cached = SHARED_REGISTRIES.get(fieldPacks);
  if (cached) return cached;
  const created = new FolderRecordProtectionRegistry(fieldPacks);
  SHARED_REGISTRIES.set(fieldPacks, created);
  return created;
}

export function profileExistenceClassification(domain: string): FolderRecordFieldClassification {
  const sourceTypes = defaultSourceTypes(domain);
  const sensitivity = sourceTypes.reduce<FieldSensitivity>((highest, sourceType) => {
    const candidate = SOURCE_AUTHORITY_TYPE_REGISTRY[sourceType].defaultSensitivity;
    return fieldSensitivityRank(candidate) > fieldSensitivityRank(highest) ? candidate : highest;
  }, 'public');
  return {
    sensitivity,
    fieldPackIds: [`profile:${domain}`],
    sourceTypes,
    origin: 'registered',
  };
}

export function defaultSourceTypes(profileId: string): SourceAuthorityType[] {
  switch (profileId) {
    case 'orgchart':
    case 'atlas-succession':
    case 'genogram':
      return ['people_identity'];
    case 'skill': return ['skill_catalog'];
    case 'credential': return ['skill_catalog'];
    case 'talent_observation': return ['talent_observation'];
    case 'process': return ['process_inventory'];
    case 'network': return ['system_inventory'];
    case 'grc-control': return ['control_risk'];
    case 'software': return ['software_design'];
    case 'relationship': return ['process_responsibility'];
    default: return ['system_inventory'];
  }
}

export function sourceTypesForRelation(predicate: string): SourceAuthorityType[] {
  const canonicalPredicate = getCanonicalPredicateForAlias(predicate) ?? predicate;
  const registeredSourceTypes = getRelationPredicateDefinition(canonicalPredicate)?.sourceTypes;
  if (registeredSourceTypes?.length) return [...registeredSourceTypes];

  // Compatibility fallback for profile-only and legacy predicates that have
  // not entered the shared canonical relation registry.
  if (predicate === 'successor_of' || predicate === 'successor_for') return ['succession_plan'];
  if (predicate === 'project_assignment') return ['project_allocation'];
  if (predicate === 'capacity_commitment') return ['capacity_commitment'];
  if (predicate === 'member_of') return ['team_membership'];
  if (predicate === 'has_skill') return ['skill_assertion'];
  if (predicate === 'requires_skill' || predicate === 'requires_credential') return ['skill_requirement'];
  if (predicate === 'holds_credential') return ['credential_assertion'];
  if (predicate === 'occupies' || predicate.includes('reports_to')) return ['position_structure'];
  if (['controlled_by', 'responsible_for', 'accountable_for', 'consulted_on', 'informed_of'].includes(predicate)) {
    return ['process_responsibility'];
  }
  if (['mitigates', 'satisfies', 'governs', 'remediates', 'finding_from'].includes(predicate)) {
    return ['control_risk'];
  }
  return ['system_inventory'];
}

/** Stable field-pack identity shared by relation writes, protected storage, and projections. */
export function fieldPackIdForRelation(predicate: string): string {
  // Successor nominations are administered through the public Succession pack.
  // Grants and propose/apply envelopes use `orgchart-succession`; projections
  // must use the same id or hierarchy-scoped Planners can draft a slate and
  // lose it after Apply when reads demand `relation:successor_of`.
  if (predicate === 'successor_of') return 'orgchart-succession';
  return `relation:${predicate}`;
}

/** Classification of the full, identity-bearing envelope at rest. */
export function orgPlanningAssertionStorageClassification(
  value: unknown,
): FolderRecordFieldClassification {
  const sourceType = assertionSourceType(value);
  return {
    sensitivity: 'manager',
    fieldPackIds: [ORG_PLANNING_ASSERTION_FIELD_PACK_ID],
    sourceTypes: sourceType ? [sourceType] : [...ORG_PLANNING_SOURCE_TYPES],
    origin: 'registered',
  };
}

/**
 * Declassifies the persisted envelope to the control metadata required to
 * compose an effective fact. Actor/reviewer identities and nested evidence
 * are never returned by the shared projection boundary.
 */
export function projectOrgPlanningAssertionMetadata(
  value: unknown,
): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const metadata = value as Record<string, unknown>;
  const projected = Object.fromEntries(
    Object.entries(metadata).filter(([key]) => PROJECTED_ASSERTION_KEYS.has(key)),
  );
  if (Object.values(projected).some((entry) => entry !== null && typeof entry === 'object')) return null;
  return typeof projected.subjectRecordId === 'string'
    && typeof projected.operation === 'string'
    ? projected
    : null;
}

function assertionSourceType(value: unknown): SourceAuthorityType | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const sourceType = (value as Record<string, unknown>).sourceType;
  return typeof sourceType === 'string' && sourceType in SOURCE_AUTHORITY_TYPE_REGISTRY
    ? sourceType as SourceAuthorityType
    : null;
}

function buildFieldIndex(
  profileId: string,
  packs: readonly FieldPack[],
): Map<string, FolderRecordFieldClassification> {
  const index = new Map<string, FolderRecordFieldClassification>();
  for (const pack of packs) {
    for (const field of pack.fields) {
      const keys = [field.id];
      if (field.namespace) keys.push(`${field.namespace}.${field.id}`);
      const classification: FolderRecordFieldClassification = {
        sensitivity: field.sensitivity ?? 'internal',
        fieldPackIds: [field.fieldPackId ?? pack.id],
        sourceTypes: normalizeSourceTypes(
          field.sourceTypes,
          sourceTypesForPack(profileId, pack.id),
        ),
        origin: 'registered',
      };
      for (const key of keys) {
        const current = index.get(key);
        if (!current || fieldSensitivityRank(classification.sensitivity) > fieldSensitivityRank(current.sensitivity)) {
          index.set(key, classification);
        }
      }
    }
  }
  return index;
}

function normalizeSourceTypes(
  sourceTypes: readonly string[] | undefined,
  fallback: SourceAuthorityType[],
): SourceAuthorityType[] {
  const valid = (sourceTypes ?? []).filter(
    (value): value is SourceAuthorityType => value in SOURCE_AUTHORITY_TYPE_REGISTRY,
  );
  return valid.length > 0 ? valid : fallback;
}

function sourceTypesForPack(profileId: string, packId: string): SourceAuthorityType[] {
  if (profileId === 'orgchart') {
    if (packId.includes('position') || packId.includes('role-profile')) return ['position_structure'];
    if (packId.includes('team')) return ['team_membership'];
    if (packId.includes('project')) return ['project_allocation'];
    if (packId.includes('workforce')) return ['capacity_commitment'];
    if (packId.includes('skills')) return ['skill_assertion', 'credential_assertion'];
    if (packId.includes('talent')) return ['talent_observation'];
    return ['people_identity'];
  }
  if (profileId === 'process') {
    if (packId.includes('compliance') || packId.includes('risk')) return ['control_risk'];
    return ['process_inventory'];
  }
  if (profileId === 'network') {
    if (packId.includes('security')) return ['access_grant'];
    return ['system_inventory'];
  }
  return defaultSourceTypes(profileId);
}

const CONTAINER_ESCALATION_MAX_DEPTH = 4;
const CONTAINER_ESCALATION_MAX_NODES = 10_000;

export interface ContainerClassification {
  sensitivity: FieldSensitivity;
  fieldPackIds: string[];
  sourceTypes: SourceAuthorityType[];
  escalated: boolean;
}

/**
 * A container value (object/array) must never pass a redaction boundary as a
 * single unit while carrying values stricter than the container's own key.
 * Escalates the classification to the maximum protected nested key found, so
 * an evidence blob like `source.rowSnapshot` follows the strictest field
 * inside it instead of its own (usually unclassified) top-level key.
 */
export function escalateClassificationForContainer(
  registry: FolderRecordProtectionRegistry,
  base: {
    sensitivity: FieldSensitivity;
    fieldPackIds: readonly string[];
    sourceTypes: readonly SourceAuthorityType[];
  },
  value: unknown,
): ContainerClassification {
  const result: ContainerClassification = {
    sensitivity: base.sensitivity,
    fieldPackIds: [...base.fieldPackIds],
    sourceTypes: [...base.sourceTypes],
    escalated: false,
  };
  const seenPackIds = new Set(result.fieldPackIds);
  const seenSourceTypes = new Set(result.sourceTypes);
  const traversal = { visited: 0, truncated: false };
  visitContainerKeys(value, 0, traversal, (key) => {
    const nested = registry.protectedNestedKeyClassification(key);
    if (!nested) return;
    if (fieldSensitivityRank(nested.sensitivity) < fieldSensitivityRank('manager')) return;
    if (fieldSensitivityRank(nested.sensitivity) > fieldSensitivityRank(result.sensitivity)) {
      result.sensitivity = nested.sensitivity;
    }
    for (const packId of nested.fieldPackIds) {
      if (!seenPackIds.has(packId)) {
        seenPackIds.add(packId);
        result.fieldPackIds.push(packId);
      }
    }
    for (const sourceType of nested.sourceTypes) {
      if (!seenSourceTypes.has(sourceType)) {
        seenSourceTypes.add(sourceType);
        result.sourceTypes.push(sourceType);
      }
    }
    result.escalated = true;
  });
  if (traversal.truncated) {
    if (fieldSensitivityRank('hr_restricted') > fieldSensitivityRank(result.sensitivity)) {
      result.sensitivity = 'hr_restricted';
    }
    if (!seenPackIds.has('unclassified:container-budget')) {
      result.fieldPackIds.push('unclassified:container-budget');
    }
    result.escalated = true;
  }
  return result;
}

/** Exact shape validation for repository-owned structural metadata. */
export function isValidStructuralFolderRecordValue(key: string, value: unknown): boolean {
  if (key === '_sourceBinding') {
    return isPlainRecord(value)
      && Object.entries(value).every(([nestedKey, nestedValue]) =>
        SOURCE_BINDING_KEYS.has(nestedKey) && isScalar(nestedValue));
  }
  if (key === 'lifecycle') {
    if (!isPlainRecord(value) || !hasOnlyKeys(value, LIFECYCLE_KEYS)) return false;
    if (typeof value.state !== 'string' || !LIFECYCLE_STATES.has(value.state)) return false;
    if (typeof value.source !== 'string' || !LIFECYCLE_SOURCES.has(value.source)) return false;
    for (const field of ['setAt', 'setBy', 'setByName', 'reason'] as const) {
      if (value[field] !== undefined && typeof value[field] !== 'string') return false;
    }
    if (value.agentRun !== undefined) {
      if (!isPlainRecord(value.agentRun) || !hasOnlyKeys(value.agentRun, new Set(['kind', 'id']))) {
        return false;
      }
      if (typeof value.agentRun.kind !== 'string' || !AGENT_RUN_KINDS.has(value.agentRun.kind)) {
        return false;
      }
      if (typeof value.agentRun.id !== 'string') return false;
    }
    return true;
  }
  if (key === '_fieldProvenance') {
    if (!isPlainRecord(value)) return false;
    return Object.values(value).every((entry) => {
      if (!isPlainRecord(entry) || !hasOnlyKeys(entry, new Set(['lastWrittenBy', 'ref', 'at']))) {
        return false;
      }
      return (entry.lastWrittenBy === 'extraction'
          || entry.lastWrittenBy === 'human'
          || entry.lastWrittenBy === 'agent')
        && typeof entry.ref === 'string'
        && typeof entry.at === 'string';
    });
  }
  return !value || typeof value !== 'object';
}

/**
 * Write-side twin of container escalation: removes protected nested keys from
 * an evidence row snapshot so the value lives only in its properly classified
 * field (raw or isolated protected storage), never duplicated inside a blob
 * with a different access path. Returns the input unchanged when nothing is
 * removed.
 */
export function sanitizeEvidenceRowSnapshot(
  registry: FolderRecordProtectionRegistry,
  snapshot: Record<string, unknown>,
): { snapshot: Record<string, unknown>; redactedKeyCount: number } {
  const safe: Record<string, unknown> = {};
  let redactedKeyCount = 0;
  for (const [key, value] of Object.entries(snapshot)) {
    const classification = registry.protectedNestedKeyClassification(key);
    if (classification
      && fieldSensitivityRank(classification.sensitivity) >= fieldSensitivityRank('manager')) {
      redactedKeyCount += 1;
      continue;
    }
    safe[key] = value;
  }
  return redactedKeyCount > 0
    ? { snapshot: safe, redactedKeyCount }
    : { snapshot, redactedKeyCount: 0 };
}

function visitContainerKeys(
  value: unknown,
  depth: number,
  traversal: { visited: number; truncated: boolean },
  visit: (key: string) => void,
): void {
  if (!value || typeof value !== 'object') return;
  if (depth >= CONTAINER_ESCALATION_MAX_DEPTH) {
    traversal.truncated = true;
    return;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      traversal.visited += 1;
      if (traversal.visited > CONTAINER_ESCALATION_MAX_NODES) {
        traversal.truncated = true;
        return;
      }
      visitContainerKeys(item, depth + 1, traversal, visit);
      if (traversal.truncated) return;
    }
    return;
  }
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    traversal.visited += 1;
    if (traversal.visited > CONTAINER_ESCALATION_MAX_NODES) {
      traversal.truncated = true;
      return;
    }
    visit(key);
    visitContainerKeys(nested, depth + 1, traversal, visit);
    if (traversal.truncated) return;
  }
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function hasOnlyKeys(value: Record<string, unknown>, allowed: ReadonlySet<string>): boolean {
  return Object.keys(value).every((key) => allowed.has(key));
}

function isScalar(value: unknown): boolean {
  return value === null
    || value === undefined
    || typeof value === 'string'
    || typeof value === 'number'
    || typeof value === 'boolean';
}

export function sensitivityForKnownProtectedKey(key: string): FieldSensitivity {
  const normalized = key.toLowerCase();
  if (normalized.includes('succession') || normalized.includes('internalnote')) return 'hr_restricted';
  if (
    normalized.includes('salary')
    || normalized.includes('pay')
    || normalized.includes('comp')
    || normalized.includes('bonus')
  ) {
    return 'hr_confidential';
  }
  return 'sensitive_pii';
}
